import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Ensure import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { Summary } from '@components/interview/analysis-components/Summary';
import { TaskOverview } from '@components/interview/analysis-components/TaskOverview';
import { getQuoteText } from '@utils/representativeQuotes';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'; // For Motivations
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GroupIcon from '@mui/icons-material/Group'; // For Group
import InsightsIcon from '@mui/icons-material/Insights';
import MapIcon from '@mui/icons-material/Map'; // For Context
import NumbersIcon from '@mui/icons-material/Numbers';
import PersonIcon from '@mui/icons-material/Person'; // For Persona
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // For Friction Points
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useParams } from 'react-router';
import { AnalysisLayout } from './layout';

/** -------------------------------------------------------------
 * PersonaTaskAnalysis – displays a portfolio-level Persona-Task analysis
 * ------------------------------------------------------------*/
export default function PersonaTaskAnalysis({ item }) {
  const [expandedPersona, setExpandedPersona] = React.useState(false);
  const [expandedTask, setExpandedTask] = React.useState(false);
  const { id } = useParams();

  if (!item) return null;

  // --- Start: Inline data handling logic ---
  let results = {};
  if (typeof item.results === 'object' && item.results !== null) {
    results = item.results; // Use directly if already an object
  } else if (typeof item.results === 'string') {
    try {
      results = JSON.parse(item.results); // Try parsing if it's a string
    } catch (err) {
      console.error('Failed to parse results JSON string:', err, 'JSON String:', item.results);
      results = {}; // Assign empty object on parsing error
    }
  } else {
    // Handle cases where results is neither object nor string (e.g., undefined, null)

    console.warn('Results field is not an object or string:', item.results);
  }
  // --- End: Inline data handling logic ---

  // Fallback if results are invalid or missing the required 'summary'
  if (Object.keys(results).length === 0 || !results.summary) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            Analysis Data Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The results for this Persona-Task analysis could not be loaded or are improperly
            formatted.
          </Typography>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Show Raw Data</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {JSON.stringify(item, null, 2)}
              </pre>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  }

  /* ───────────────────── Top Friction Points card ───────────────────── */
  const TopFrictionPoints = () => {
    if (!results.top_friction_points?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <WarningAmberIcon color="error" /> Top Friction Points
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {results.top_friction_points.map((friction, index) => (
              <Chip
                key={index}
                label={`${friction.friction_point} (${friction.frequency})`}
                size="small"
                variant="outlined"
                color="error"
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  /* ───────────────────── Priority Insights card ───────────────────── */
  const PriorityInsights = () => {
    if (!results.priority_insights?.length > 0) return null;
    return (
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <InsightsIcon color="primary" /> Priority Insights
          </Typography>
          <List dense>
            {results.priority_insights.map((insight, i) => (
              <ListItem key={i} disableGutters>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <InsightsIcon fontSize="small" color="disabled" />
                </ListItemIcon>
                <ListItemText primary={insight} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  /* ──────────────────────── Persona Overview Accordions ─────────────────────── */
  const PersonaOverview = () => {
    if (!results.persona_overview?.length > 0) return null;

    const handleChange = (panel) => (event, isExpanded) => {
      setExpandedPersona(isExpanded ? panel : false); // Use top-level state setter
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          Persona Overview
        </Typography>
        {results.persona_overview.map((persona, index) => {
          const panelId = `persona-panel-${index}`;
          return (
            <Accordion
              key={panelId}
              expanded={expandedPersona === panelId} // Use top-level state
              onChange={handleChange(panelId)}
              sx={{ mb: 1 }}
              variant="outlined"
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
              >
                <PersonIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ flexShrink: 2, fontWeight: 500 }}>
                  {persona.persona_label}
                </Typography>
                <Chip
                  icon={<NumbersIcon />}
                  label={`Prevalence: ${persona.prevalence}`}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                {/* Dominant Context */}
                {persona.dominant_context && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <MapIcon fontSize="small" color="action" /> Dominant Context
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', pl: 3 }}>
                      {persona.dominant_context}
                    </Typography>
                  </Box>
                )}

                {/* Common Motivations */}
                {persona.common_motivations?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <DirectionsRunIcon fontSize="small" color="secondary" /> Common Motivations
                    </Typography>
                    <List dense disablePadding sx={{ pl: 3 }}>
                      {persona.common_motivations.map((motivation, i) => (
                        <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ minWidth: 20, mt: 0.5 }}>
                            <DirectionsRunIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={motivation}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Representative Quotes */}
                {persona.representative_quotes?.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <FormatQuoteIcon fontSize="small" color="action" /> Representative Quotes
                    </Typography>
                    <List dense disablePadding sx={{ pl: 3 }}>
                      {persona.representative_quotes.map((quote, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 20 }}>
                            <FormatQuoteIcon
                              sx={{ fontSize: 16, transform: 'scaleX(-1)', color: 'text.disabled' }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={getQuoteText(quote)}
                            primaryTypographyProps={{
                              variant: 'body2',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };

  return (
    <AnalysisLayout results={results} item={{ name: 'Persona & Task Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <GroupIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Persona & Task Analysis
            {item.analysis_lens_type && (
              <AnalysisLensHelperText lensName={item.analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary results={results} item={item} />
        <TopFrictionPoints />
        <PriorityInsights />
        <PersonaOverview />
        <TaskOverview
          results={results}
          setExpandedTask={setExpandedTask}
          expandedTask={expandedTask}
        />
        <IncludedTranscripts item={item} id={id} />
      </CardContent>
    </AnalysisLayout>
  );
}

PersonaTaskAnalysis.propTypes = {
  item: PropTypes.object.isRequired,
};
